<template>
  <div class="proposal-cards">
    <!-- Table Toolbar -->
    <table-toolbar
      :show-title="true"
      title="Proposals"
      :show-count="true"
      :current-count="orderedProposals.length"
      :total-count="proposalCount"
      :searchable="true"
      :search-query="proposalsSearchQuery"
      @search-change="proposalsSearchQuery = $event"
    >
      <template #action-buttons>
        <themed-button
          v-if="canCreateProposal"
          variant="default"
          size="default"
          @click="openCreateModal"
          aria-label="Create proposal"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>Create Proposal</span>
        </themed-button>
      </template>
    </table-toolbar>

    <!-- Table Section -->
    <themed-table
      :data="orderedProposals"
      :columns="tableColumns"
      :loading="isLoading"
      hoverable
      @row-click="handleRowClick"
      :show-row-details="true"
      row-details-title="Proposal Details"
    />

    <!-- Create Proposal Modal -->
    <create-proposal-modal
      v-if="projectId"
      :project-id="projectId"
      @created="handleProposalCreated"
    />
  </div>
</template>

<script>
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';
import CreateProposalModal from '../projects/CreateProposalModal.vue';
import { formatShorthandCurrency } from '../../../../utils/formatters';

export default {
  name: 'ProposalCards',
  
  components: {
    ThemedTable,
    ThemedButton,
    ThemeIcon,
    TableToolbar,
    CreateProposalModal
  },
  
  props: {
    projectId: {
      type: Number,
      default: null
    },
    proposals: {
      type: Array,
      required: true,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    canCreate: {
      type: Boolean,
      default: null
    }
  },
  
  data() {
    return {
      proposalsSearchQuery: ''
    };
  },
  
  computed: {
    /**
     * Table column configuration for ThemedTable
     */
    tableColumns() {
      return [
        {
          field: 'building.image.view_url',
          label: 'Image',
          type: 'image',
          width: '120px',
          altField: 'building.address_line_1',
          sortable: false,
          showInTable: true,
          alwaysVisible: true,
          formatter: (value, row) => {
            const imageUrl = value || row.building?.image?.view_url || '/img/placeholder-building.jpg';
            return `<img src="${imageUrl}" alt="${row.building?.address_line_1 || 'Building'}" style="width: 80px; height: 60px; object-fit: cover; object-position: center; display: block; border-radius: 8px;" />`;
          }
        },
        {
          field: 'building',
          label: 'Existing Proposals',
          type: 'custom',
          sortable: false,
          formatter: (value, row) => {
            if (!value) return '-';
            return `
              <div class="proposal-cards__building-info">
                <a href="${row.view_url}" class="proposal-cards__building-address">
                  ${value.address_line_1}
                </a>
                <span class="proposal-cards__building-location">
                  ${value.city}, ${value.state}
                </span>
              </div>
            `;
          }
        },
        {
          field: 'revision_number',
          label: 'Rev.',
          type: 'number',
          sortable: true,
          showInTable: false,
          align: 'right'
        },
        // Proposal money columns are stored in cents. The imported
        // formatShorthandCurrency does no conversion, unlike the global
        // formatCurrency mixin, so each formatter converts before display.
        {
          field: 'total_lease_cost',
          label: 'Total Lease ($)',
          type: 'currency',
          showInTable: false,
          sortable: true,
          align: 'right',
          formatter: (value) => formatShorthandCurrency((value || 0) / 100)
        },
        {
          field: 'effective_rent_prsf',
          label: 'Eff. Rent PRSF',
          type: 'currency',
          sortable: true,
          align: 'right',
          formatter: (value) => formatShorthandCurrency((value || 0) / 100)
        },
        {
          field: 'average_rent_pusf',
          label: 'Avg PUSF ($)',
          type: 'currency',
          sortable: true,
          align: 'right',
          formatter: (value) => formatShorthandCurrency((value || 0) / 100)
        },
        {
          field: 'average_monthly_cost',
          label: 'Avg Monthly Cost',
          type: 'currency',
          sortable: true,
          align: 'right',
          formatter: (value) => formatShorthandCurrency((value || 0) / 100)
        },
        {
          field: 'npv',
          label: 'NPV',
          type: 'currency',
          showInTable: false,
          sortable: true,
          align: 'right',
          formatter: (value) => formatShorthandCurrency((value || 0) / 100)
        }
      ];
    },
    
    /**
     * Returns filtered proposals based on search query
     */
    filteredProposals() {
      let proposals = this.proposals;
      
      // Apply search filter
      if (this.proposalsSearchQuery) {
        const query = this.proposalsSearchQuery.toLowerCase();
        proposals = proposals.filter(proposal => 
          proposal.building?.address_line_1?.toLowerCase().includes(query) ||
          proposal.building?.city?.toLowerCase().includes(query) ||
          proposal.building?.state?.toLowerCase().includes(query) ||
          proposal.revision_number?.toString().includes(query)
        );
      }
      
      return proposals;
    },
    
    /**
     * Returns proposals ordered by building_id and revision_number
     */
    orderedProposals() {
      return _.orderBy(this.filteredProposals, ['building_id', 'revision_number']);
    },
    
    /**
     * Returns count of proposals for header display
     */
    proposalCount() {
      return this.proposals.length;
    },
    
    /**
     * Checks if user has permission to create proposals
     */
    canCreateProposal() {
      // Use prop if provided, otherwise check gate
      if (this.canCreate !== null) {
        return this.canCreate;
      }
      return this.$gate.can('create', 'project');
    }
  },
  
  methods: {
    /**
     * Handles row click to navigate to proposal detail page
     * @param {Object} event - Row click event with row data
     */
    handleRowClick(event) {
      if (event.row && event.row.view_url) {
        window.location.href = event.row.view_url;
      }
    },
    
    /**
     * Opens the create proposal modal
     */
    openCreateModal() {
      this.$modal.show('create-proposal');
    },
    
    /**
     * Handles proposal creation success
     * Adds the new proposal to the list immediately
     * @param {Object} proposal - The newly created proposal object
     */
    handleProposalCreated(proposal) {
      this.$modal.hide('create-proposal');
      
      // Emit event to parent with the new proposal data
      this.$emit('proposal-created', proposal);
      
      // Show success notification
      this.$notify({
        type: 'success',
        title: 'Success',
        text: 'Proposal created successfully'
      });
    }
  }
};
</script>
